import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  FaTabletAlt,
  FaDownload,
  FaShareSquare,
  FaPlusSquare,
  FaExpand,
  FaCompress,
  FaTimes,
  FaDesktop,
  FaApple,
  FaAndroid,
  FaWindows,
  FaCheck,
  FaCopy,
  FaArrowDown
} from "react-icons/fa";

const KioskInstallModal = ({
  isOpen,
  onClose,
  canInstall,
  onPromptInstall,
  isStandalone,
  isFullscreen,
  onToggleFullscreen,
  platform
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopyLink = () => {
    try {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success("✓ Kiosk link copied to clipboard!");
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      toast.info("Please copy URL from address bar.");
    }
  };

  const isIOS = platform === "ios";
  const isMacSafari = platform === "mac-safari" || platform === "safari";

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-lg flex items-center justify-center p-3 sm:p-4 animate-fadeIn select-none overflow-y-auto">
      <div className="kiosk-apple-card border border-white/20 p-5 sm:p-6 rounded-3xl max-w-lg w-full shadow-2xl text-white space-y-5 my-auto max-h-[92dvh] overflow-y-auto animate-scaleUp">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 pb-3.5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-orange-500 via-amber-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-orange-500/20 flex-shrink-0">
              <FaTabletAlt size={18} className="text-white" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-black kiosk-text-gradient-hero">
                Install Kiosk Standalone App
              </h3>
              <p className="text-[11px] text-slate-400">
                Eliminate browser bars &amp; run as a native showroom display
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white flex items-center justify-center transition-colors cursor-pointer flex-shrink-0"
          >
            <FaTimes size={13} />
          </button>
        </div>

        {/* Standalone Status Badge */}
        <div className="p-3 rounded-2xl bg-black/40 border border-white/10 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <span
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                isStandalone ? "bg-emerald-400 animate-pulse" : "bg-amber-400"
              }`}
            />
            <div className="truncate">
              <span className="text-xs font-bold block truncate">
                {isStandalone
                  ? "✓ Running in Standalone PWA Mode"
                  : "Running in Standard Web Browser"}
              </span>
              <span className="text-[10px] text-slate-400 block truncate">
                {isStandalone
                  ? "Browser navigation bars are hidden"
                  : "Add to home screen to unlock true fullscreen tablet mode"}
              </span>
            </div>
          </div>

          <button
            onClick={onToggleFullscreen}
            className="px-2.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-[11px] font-bold flex items-center gap-1.5 transition-colors cursor-pointer border border-white/10 flex-shrink-0"
            title="Toggle Browser Fullscreen"
          >
            {isFullscreen ? <FaCompress size={11} /> : <FaExpand size={11} />}
            <span>{isFullscreen ? "Exit" : "Fullscreen"}</span>
          </button>
        </div>

        {/* Platform-Specific Step Guide */}
        <div className="space-y-3">
          <h4 className="text-xs font-black uppercase tracking-widest text-cyan-400 flex items-center gap-2">
            {isIOS ? (
              <>
                <FaApple size={14} /> <span>iPhone &amp; iPad Safari Instructions</span>
              </>
            ) : isMacSafari ? (
              <>
                <FaApple size={14} /> <span>Mac Safari (macOS Sonoma+)</span>
              </>
            ) : platform === "android" ? (
              <>
                <FaAndroid size={14} /> <span>Android &amp; Chrome Instructions</span>
              </>
            ) : (
              <>
                <FaDesktop size={14} /> <span>Desktop &amp; Tablet Instructions</span>
              </>
            )}
          </h4>

          {isIOS ? (
            <div className="space-y-2.5 text-xs text-slate-300">
              <div className="p-3 rounded-2xl bg-white/5 border border-cyan-500/30 flex items-start gap-3 shadow-inner">
                <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  1
                </span>
                <div>
                  <span className="font-bold text-white">Tap Safari's Share Icon</span> located in the{" "}
                  <strong className="text-cyan-300">bottom bar</strong> on iPhone or top toolbar on iPad (
                  <FaShareSquare className="inline text-cyan-400 mx-1" />
                  <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded font-mono">
                    [↑]
                  </span>
                  ).
                </div>
              </div>

              <div className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  2
                </span>
                <div>
                  Scroll down the share sheet and tap{" "}
                  <strong className="text-white">"Add to Home Screen"</strong> (
                  <FaPlusSquare className="inline text-emerald-400 mx-1" />
                  <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded font-mono">
                    [+]
                  </span>
                  ).
                </div>
              </div>

              <div className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  3
                </span>
                <div>
                  Tap <strong className="text-white">"Add"</strong> in the top right corner. Then open the new{" "}
                  <strong className="text-cyan-300 font-black">"SaleSense Kiosk"</strong> icon on your home screen!
                </div>
              </div>

              {/* Animated Pointer down toward iPhone Safari Share toolbar */}
              <div className="p-2.5 rounded-xl bg-cyan-950/40 border border-cyan-500/20 flex items-center justify-center gap-2 text-[11px] text-cyan-300 font-bold animate-bounce">
                <FaArrowDown size={12} />
                <span>Look for the Share icon [↑] at the bottom of your iPhone screen</span>
                <FaArrowDown size={12} />
              </div>
            </div>
          ) : isMacSafari ? (
            <div className="space-y-2.5 text-xs text-slate-300">
              <div className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 font-bold flex items-center justify-center flex-shrink-0">
                  1
                </span>
                <div>
                  In Safari top menu bar, click <strong className="text-white">File</strong> &gt;{" "}
                  <strong className="text-cyan-300">"Add to Dock..."</strong>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 font-bold flex items-center justify-center flex-shrink-0">
                  2
                </span>
                <div>
                  Click <strong className="text-white">"Add"</strong> to launch SaleSense as an isolated desktop application in its own window.
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Always Available Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2.5">
                <button
                  onClick={async () => {
                    const success = await onPromptInstall();
                    if (success) {
                      onClose();
                    } else {
                      onToggleFullscreen();
                      toast.info("Look for the Install icon (↓) in your browser address bar.");
                    }
                  }}
                  className="flex-1 py-3.5 px-4 rounded-2xl kiosk-apple-btn-primary text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-transform cursor-pointer"
                >
                  <FaDownload size={13} />
                  <span>{canInstall ? "Install Kiosk App Now (1-Click)" : "Install Kiosk App / Launch"}</span>
                </button>

                <button
                  onClick={() => {
                    onToggleFullscreen();
                    onClose();
                  }}
                  className="py-3.5 px-4 rounded-2xl kiosk-apple-btn-secondary text-slate-200 font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-transform cursor-pointer border border-white/10"
                >
                  {isFullscreen ? <FaCompress size={12} /> : <FaExpand size={12} />}
                  <span>{isFullscreen ? "Exit Fullscreen" : "Fullscreen Kiosk"}</span>
                </button>
              </div>

              {/* Step-by-Step Address Bar Instructions */}
              <div className="space-y-2 text-xs text-slate-300 pt-1">
                <div className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 font-bold flex items-center justify-center flex-shrink-0">
                    1
                  </span>
                  <div>
                    Click the <strong className="text-white">Install Icon</strong> (
                    <FaDownload className="inline text-cyan-400 mx-1" />) in your browser's address bar (Chrome, Edge, Brave) or tap the button above.
                  </div>
                </div>
                <div className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 font-bold flex items-center justify-center flex-shrink-0">
                    2
                  </span>
                  <div>
                    Click <strong className="text-white">"Install"</strong> when prompted to add the standalone application shortcut.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick Link Copy & Dedicated Hardware Tip */}
        <div className="p-3 rounded-2xl bg-cyan-950/30 border border-cyan-500/20 text-[11px] text-slate-300 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold text-cyan-300 flex items-center gap-1.5">
              <FaWindows size={11} /> Dedicated Dealership Hardware:
            </span>
            <button
              onClick={handleCopyLink}
              className="px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-cyan-300 hover:text-white font-bold text-[10px] flex items-center gap-1 transition-colors cursor-pointer border border-white/10"
            >
              {copied ? <FaCheck size={10} className="text-emerald-400" /> : <FaCopy size={10} />}
              <span>{copied ? "Copied!" : "Copy Link"}</span>
            </button>
          </div>
          <p className="text-slate-400">
            Showroom tablets: Lock with <span className="text-white font-semibold">iPad Guided Access</span> (triple-click power button) or Windows Chrome Kiosk flag:{" "}
            <code className="text-cyan-200 bg-black/50 px-1 py-0.5 rounded font-mono text-[10px]">--kiosk --app=URL</code>
          </p>
        </div>

        {/* Footer Close */}
        <div className="flex justify-end pt-1">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl kiosk-apple-btn-secondary text-xs font-bold cursor-pointer hover:scale-105 transition-transform"
          >
            Got It
          </button>
        </div>
      </div>
    </div>
  );
};

export default KioskInstallModal;

